/* eslint-disable react/no-unused-prop-types */
import type {NavigateFunction} from 'react-router'
import {fromMaybe, maybe} from '@freckle/maybe'
import type {LinksT} from '@freckle/educator-entities/ts/common/helpers/ajax'
import {
  validateGrade,
  gradeIntAsString,
  gradeStringToInt
} from '@freckle/educator-entities/ts/common/helpers/common-helper'
import type {IdentityProviderT} from '@freckle/educator-entities/ts/roster/identity-provider'
import {getIdpLabel} from '@freckle/educator-entities/ts/roster/identity-provider'
import type {SchoolAttrs} from '@freckle/educator-entities/ts/roster/models/school'
import {
  Table,
  cellFromRow,
  aligned,
  disableSortBy,
  idAccessorFromProp
} from '@freckle/educator-materials/ts/components/table'
import {Text} from '@freckle/educator-materials/ts/components/typography'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import {
  Grades,
  LinkedTeacher,
  LinkedStudentCount
} from '@freckle/school/ts/common/components/table/cell'
import type {SummaryT} from '@freckle/school/ts/common/models/summary'
import type {CourseTabFilterQueryParamsT} from '@freckle/school/ts/common/routers/routes'
import {Routes, COURSE_FILTER_QUERY_PARAM_TAG} from '@freckle/school/ts/common/routers/routes'
import {fetchCourseTableRows} from '@freckle/school/ts/manage-teachers/api-helpers/api-course-table'
import type {IdentityProviderOrNoneT} from '@freckle/school/ts/manage-teachers/api-helpers/filters'
import {ApiControlledTabContainer} from '@freckle/school/ts/manage-teachers/components/api-controlled-table'
import type {TableParamsT} from '@freckle/school/ts/manage-teachers/components/api-controlled-table'
import type {CourseTabFilteringT} from '@freckle/school/ts/manage-teachers/components/course-tab'
import CourseTabFilter from '@freckle/school/ts/manage-teachers/components/course-tab/course-tab-filter'
import {FilterInput} from '@freckle/school/ts/manage-teachers/components/filter-input'
import {SourceDropdown} from '@freckle/school/ts/manage-teachers/components/source-dropdown'
import type {
  TeacherSimplifiedAttrT,
  StudentSimplifiedT
} from '@freckle/school/ts/manage-teachers/models/course-table-row'
import {headerVerticalAlign} from '../teacher-tab/teacher-table/teacher-table.module.scss'

type TableRowT = {
  id: number
  name: string
  teacher: TeacherSimplifiedAttrT
  students: Array<StudentSimplifiedT>
  grades: Array<number>
  sisId: string | undefined | null
  identityProvider: IdentityProviderT | undefined | null
}

export type Props = {
  currentSchoolIds: Array<number> | undefined | null
  schools: Array<SchoolAttrs>
  routerHistory: NavigateFunction
  tableParams: TableParamsT<CourseTabFilteringT>
  summary: SummaryT
}

export function CourseTabContainer(props: Props) {
  const {currentSchoolIds, schools, tableParams, routerHistory, summary} = props
  const {t} = useSafeTranslation()

  function fetchTableRows(newTableParams: TableParamsT<CourseTabFilteringT>): Promise<{
    links: LinksT
    rows: Array<TableRowT>
    totalCount: number
  }> {
    const allSchoolIds = schools.map(s => s.id)
    const schoolIdsToUse = fromMaybe(() => allSchoolIds, currentSchoolIds)

    return fetchCourseTableRows(schoolIdsToUse, newTableParams).then(
      ({courseTableRows, links, totalCount}) => {
        return {links, rows: courseTableRows, totalCount}
      }
    )
  }

  function onUpdate(newTableParams: TableParamsT<CourseTabFilteringT>) {
    const {additionalFilters} = newTableParams

    const url = Routes.dashboard({
      schoolIds: currentSchoolIds,
      activeTab: 'course-tab',
      position: newTableParams.position,
      searchValue: newTableParams.searchValue,
      limit: newTableParams.limit,
      additionalFilters: buildCourseTabFiltersQueryParams(newTableParams, additionalFilters)
    })
    routerHistory(url)
  }

  const {additionalFilters} = tableParams

  const filtersNode = (
    <CourseTabFilter courseTabFilter={additionalFilters} currentSchoolIds={currentSchoolIds} />
  )

  const initialFilters = getInitialFilters(tableParams)

  // Preserve pagination limit
  const paginationLimit = tableParams.limit !== null ? {limit: tableParams.limit} : {}

  return (
    <ApiControlledTabContainer
      name="course"
      tableParams={tableParams}
      routerHistory={routerHistory}
      fetchRows={fetchTableRows}
      onUpdate={onUpdate}
      filters={filtersNode}
      render={({paginationPanel: PaginationPanel}, rows) => (
        <>
          <Table
            data={rows}
            columns={[
              {
                Header: (
                  <>
                    <Text style="label-1">
                      {t('MANAGE_TEACHERS_COURSE_TAB_COURSE_NAME_TABLE_HEADER')}
                    </Text>
                    <FilterInput
                      initValue={initialFilters.courseName}
                      routeFn={courseName =>
                        routerHistory(
                          Routes.dashboard({
                            schoolIds: currentSchoolIds,
                            activeTab: 'course-tab',
                            additionalFilters: {
                              tag: COURSE_FILTER_QUERY_PARAM_TAG,
                              courseName
                            },
                            ...paginationLimit
                          })
                        )
                      }
                    />
                  </>
                ),
                widthPx: 200,
                ...idAccessorFromProp<TableRowT>('name')
              },
              {
                Header: (
                  <>
                    <Text style="label-1">
                      {t('MANAGE_TEACHERS_COURSE_TAB_TEACHER_NAME_TABLE_HEADER')}
                    </Text>
                    <FilterInput
                      initValue={initialFilters.teacherName}
                      routeFn={teacherName =>
                        routerHistory(
                          Routes.dashboard({
                            schoolIds: currentSchoolIds,
                            activeTab: 'course-tab',
                            additionalFilters: {
                              tag: COURSE_FILTER_QUERY_PARAM_TAG,
                              teacherName
                            },
                            ...paginationLimit
                          })
                        )
                      }
                    />
                  </>
                ),
                Cell: cellFromRow((row: TableRowT) => <LinkedTeacher row={row} />),
                widthPx: 200,
                ...idAccessorFromProp<TableRowT>('teacher')
              },
              {
                Header: (
                  <>
                    <Text style="label-1">
                      {t('MANAGE_TEACHERS_COURSE_TAB_GRADES_TABLE_HEADER')}
                    </Text>
                    <FilterInput
                      initValue={initialFilters.studentGrade}
                      routeFn={studentGrade =>
                        routerHistory(
                          Routes.dashboard({
                            schoolIds: currentSchoolIds,
                            activeTab: 'course-tab',
                            additionalFilters: {
                              tag: COURSE_FILTER_QUERY_PARAM_TAG,
                              studentGrade
                            },
                            ...paginationLimit
                          })
                        )
                      }
                      parseInput={s => gradeIntAsString(gradeStringToInt(s))}
                      validateInput={validateGrade}
                    />
                  </>
                ),
                Cell: cellFromRow(({grades}: TableRowT) => <Grades grades={grades} />),
                widthPx: 150,
                ...idAccessorFromProp<TableRowT>('grades')
              },
              {
                Header: (
                  <Text style="label-1" className={headerVerticalAlign}>
                    {t('MANAGE_TEACHERS_COURSE_TAB_STUDENTS_TABLE_HEADER')}
                  </Text>
                ),
                Cell: cellFromRow((row: TableRowT) => (
                  <LinkedStudentCount row={row} currentSchoolIds={currentSchoolIds} />
                )),
                widthPx: 100,
                ...idAccessorFromProp<TableRowT>('students')
              },
              {
                Header: (
                  <SourceDropdown
                    id="dashboard-courses-source-selector"
                    summary={{tag: 'for-course', ...summary}}
                    initSources={initialFilters.sources}
                    routeFn={identityProviders =>
                      routerHistory(
                        Routes.dashboard({
                          schoolIds: currentSchoolIds,
                          activeTab: 'course-tab',
                          additionalFilters: {
                            tag: COURSE_FILTER_QUERY_PARAM_TAG,
                            identityProviders
                          },
                          ...paginationLimit
                        })
                      )
                    }
                    labelText={t('MANAGE_TEACHERS_COURSE_TAB_ROSTERED_BY_TABLE_HEADER')}
                  />
                ),
                Cell: cellFromRow(({identityProvider: idp}: TableRowT) => (
                  <>{getIdpLabel(t, idp)}</>
                )),
                widthPx: 150,
                ...idAccessorFromProp<TableRowT>('identityProvider')
              }
            ].map(c => ({...aligned('left'), ...c, disableSortBy}))}
          />
          <PaginationPanel components={{sizePerPageDropdown: <></>, pageList: <></>}} />
        </>
      )}
    />
  )
}

function buildCourseTabFiltersQueryParams(
  tableParams: TableParamsT<CourseTabFilteringT>,
  additionalFilters?: CourseTabFilteringT | null
): CourseTabFilterQueryParamsT | undefined | null {
  if (additionalFilters === null || additionalFilters === undefined) {
    return null
  }
  const initialFilters = getInitialFilters(tableParams)
  return {
    tag: COURSE_FILTER_QUERY_PARAM_TAG,
    courseName: initialFilters.courseName,
    teacherName: initialFilters.teacherName,
    studentGrade: initialFilters.studentGrade
  }
}

type InitialFiltersT = {
  courseName: string
  teacherName: string
  studentGrade: string
  sources: Array<IdentityProviderOrNoneT>
}

function getInitialFilters(tableParams: TableParamsT<CourseTabFilteringT>): InitialFiltersT {
  const {additionalFilters} = tableParams

  const empty = () => ''
  const emptyArray = () => []

  return {
    courseName: maybe(empty, filters => fromMaybe(empty, filters.courseName), additionalFilters),
    teacherName: maybe(empty, filters => fromMaybe(empty, filters.teacherName), additionalFilters),
    studentGrade: maybe(
      empty,
      filters => fromMaybe(empty, filters.studentGrade),
      additionalFilters
    ),
    sources: maybe(
      emptyArray,
      filters => fromMaybe(emptyArray, filters.identityProviders),
      additionalFilters
    )
  }
}
